import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Dialog, DialogContent, IconButton, Box, Typography, CircularProgress, Snackbar, Alert } from '@mui/material'
import { FiX, FiCheckCircle } from 'react-icons/fi'
import { membershipApi } from '@/services/api'

interface AgreementSigningModalProps {
  open: boolean
  // Agency id — passed to membershipApi.getAgencyAgreementSignUrl to fetch
  // the embedded DocuSign signing URL once the modal opens.
  agencyId: string | null | undefined
  onClose: () => void
  onSigned: () => void
  agreementTitle?: string
  agreementSubtitle?: string
}

const AgreementSigningModal: React.FC<AgreementSigningModalProps> = ({
  open,
  agencyId,
  onClose,
  onSigned,
  agreementTitle = 'Agency Platform Subscription Agreement',
  agreementSubtitle = 'Version 1.0 · Confidential — Agency Use Only',
}) => {
  const [signingUrl, setSigningUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // Track iframe loads - first load is the signing page, a second load means
  // DocuSign navigated on to its completion page.
  const iframeLoadCountRef = useRef(0)
  const hasSubmittedRef = useRef(false)

  const handleAutoSubmit = useCallback(async () => {
    if (hasSubmittedRef.current || submitting || !agencyId) return
    hasSubmittedRef.current = true
    setSubmitting(true)

    try {
      await membershipApi.updateAgencySignature(agencyId)
      onSigned()
    } catch (err) {
      console.error('Failed to record agency signature:', err)
      setErrorMessage('Signed, but we could not save this to your account. Please try again.')
      hasSubmittedRef.current = false
      setSubmitting(false)
    }
  }, [agencyId, onSigned, submitting])

  const handleIframeLoad = useCallback(() => {
    iframeLoadCountRef.current += 1
    if (iframeLoadCountRef.current >= 2) {
      handleAutoSubmit()
    }
  }, [handleAutoSubmit])

  useEffect(() => {
    if (!open) return

    const handleMessage = (event: MessageEvent) => {
      const data = event.data

      if (typeof data === 'string') {
        if (
          data.includes('signing_complete') ||
          data.includes('signingComplete') ||
          data.includes('Signing Complete')
        ) {
          handleAutoSubmit()
          return
        }
      }

      if (typeof data === 'object' && data !== null) {
        const eventType = data.event || data.type || data.action || ''
        const status = data.status || ''

        if (
          eventType === 'signing_complete' ||
          eventType === 'signingComplete' ||
          status === 'signing_complete' ||
          data.signingComplete === true
        ) {
          handleAutoSubmit()
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [open, handleAutoSubmit])

  const fetchSigningUrl = async (id: string) => {
    try {
      const data = await membershipApi.getAgencyAgreementSignUrl(id)
      setSigningUrl(data.signingUrl)
    } catch (err) {
      console.error('Failed to load DocuSign signing URL', err)
      setErrorMessage('Failed to load signing document. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (loading || submitting) return
    onClose()
  }

  useEffect(() => {
    if (open && agencyId) {
      setSubmitting(false)
      setErrorMessage('')
      setLoading(true)
      setSigningUrl('')
      iframeLoadCountRef.current = 0
      hasSubmittedRef.current = false
      fetchSigningUrl(agencyId)
    }
  }, [open, agencyId])

  return (
    <>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={5000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', height: '85vh' } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            background: 'linear-gradient(135deg, #1E1743 0%, #2d235f 100%)',
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '16.5px' }}>
              {agreementTitle}
            </Typography>
            <Typography sx={{ color: '#c4baef', fontSize: '12.5px', mt: 0.25 }}>
              {agreementSubtitle}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={loading || submitting}
            sx={{ color: '#c4baef', '&:hover': { background: 'rgba(255,255,255,0.1)', color: '#fff' } }}
          >
            <FiX />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, bgcolor: '#EEF1F4' }}>
          {loading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 2, color: '#7C8794' }}>
              <CircularProgress sx={{ color: '#7A6FA1' }} size={40} />
              <Typography>Loading agreement document…</Typography>
            </Box>
          )}

          {!loading && submitting && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                p: 4,
                background: 'linear-gradient(135deg, #f3f0ff 0%, #ede9f8 100%)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  mb: 3,
                  background: 'linear-gradient(135deg, #7A6FA1 0%, #4B3E7C 100%)',
                  borderRadius: '50%',
                  boxShadow: '0 8px 24px rgba(74,62,124,0.35)',
                }}
              >
                <FiCheckCircle style={{ fontSize: 36, color: '#fff' }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '22px', color: '#1E1743', mb: 1.5 }}>
                Signing Complete!
              </Typography>
              <Typography sx={{ color: '#8A82A5', textAlign: 'center', maxWidth: 360, mb: 3 }}>
                Please wait while we confirm your signature…
              </Typography>
              <CircularProgress size={32} sx={{ color: '#7A6FA1' }} />
            </Box>
          )}

          {!loading && !submitting && signingUrl && (
            <iframe
              src={signingUrl}
              style={{ flex: 1, width: '100%', border: 0, minHeight: 0 }}
              title="DocuSign Subscription Agreement"
              onLoad={handleIframeLoad}
            />
          )}
        </DialogContent>

        {!loading && !submitting && signingUrl && (
          <Box sx={{ flexShrink: 0, px: 2.5, py: 1.25, borderTop: '1px solid #e8e4f3', bgcolor: '#f8f7fd' }}>
            <Typography sx={{ fontSize: '13px', color: '#8A82A5', fontStyle: 'italic' }}>
              Complete signing in the document above and click &ldquo;Finish&rdquo;.
            </Typography>
          </Box>
        )}
      </Dialog>
    </>
  )
}

export default AgreementSigningModal
